import type { SortModelItem } from 'ag-grid-community';

/**
 * Converts AG Grid row bounds into a normalized offset/limit tuple.
 * Guarantees limit is at least 1 to avoid accidental empty-range API calls.
 */
export function buildOffsetLimit(startRow = 0, endRow = startRow + 100) {
  return {
    offset: startRow,
    limit: Math.max(1, endRow - startRow),
  };
}

/**
 * Builds a single-column API sort payload from AG Grid's sort model.
 * The first sort entry wins; returns undefined when no sort is active.
 */
export function buildSingleSort(
  sortModel: SortModelItem[],
): Record<string, 'ASC' | 'DESC'> | undefined {
  if (!Array.isArray(sortModel) || sortModel.length === 0) {
    return undefined;
  }
  const currentSort = sortModel[0];
  return {
    [currentSort.colId]: currentSort.sort === 'desc' ? 'DESC' : 'ASC',
  };
}

/**
 * Reads and trims a text filter from an AG Grid filter model.
 * Useful for shared `q`-style search filters.
 */
export function readTrimmedTextFilter(
  filterModel: Record<string, unknown> | null | undefined,
  key = 'q'
): string | undefined {
  if (!filterModel) {
    return undefined;
  }
  const raw = (filterModel[key] as { filter?: unknown } | undefined)?.filter;
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed || undefined;
}

/**
 * Reads and trims a URL param from the current URL filter object.
 * Supports either `string` or `string[]` URL param values.
 */
export function readTrimmedUrlParam(
  urlFilter: Record<string, unknown> | null | undefined,
  key: string
): string | undefined {
  const raw = urlFilter?.[key];
  const value =
    typeof raw === 'string'
      ? raw
      : Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'string'
      ? raw[0]
      : undefined;
  const trimmed = value?.trim();
  return trimmed || undefined;
}

/**
 * Extracts selected values from AG Grid set filters for the provided column keys.
 * Returns only keys with valid non-empty string arrays.
 */
export function pickSetFilterValuesFromModel(
  filterModel: Record<string, unknown> | null | undefined,
  keys: readonly string[]
): Record<string, string[]> {
  if (!filterModel) {
    return {};
  }

  return keys.reduce<Record<string, string[]>>((acc, key) => {
    const model = filterModel[key] as { filterType?: string; values?: unknown } | undefined;
    if (
      model?.filterType === 'set' &&
      Array.isArray(model.values) &&
      model.values.length > 0 &&
      model.values.every((v) => typeof v === 'string')
    ) {
      acc[key] = model.values;
    }
    return acc;
  }, {});
}

/**
 * Converts URL params into API set-filter payload shape (`{ key: { $in: [...] } }`)
 * for the provided keys.
 */
export function pickUrlSetFilters(
  urlFilter: Record<string, unknown> | null | undefined,
  keys: readonly string[]
): Record<string, { $in: string[] }> {
  if (!urlFilter) {
    return {};
  }

  return keys.reduce<Record<string, { $in: string[] }>>((acc, key) => {
    const raw = urlFilter[key];
    if (raw === undefined || raw === null) {
      return acc;
    }
    const normalized = Array.isArray(raw) ? raw : [raw];
    const values = normalized.filter((value): value is string => typeof value === 'string');
    if (values.length > 0) {
      acc[key] = { $in: values };
    }
    return acc;
  }, {});
}
